import { computed, ref } from 'vue';
import { createEventHook } from '@vueuse/core';

import { reportingService } from '@/common/reporting';
import { deepClone, waitFor } from '@/common/utils';
import { i18n, isSupported, setConversationLocaleOverride, tConversation } from '@/plugins/i18n';
import { switcher } from '@/common/switcher';
import { AiResponseAction, ExtendedSelectedTransStatement } from '@/pleased-sdk/PleasedApi/models.pleased';
import type { MessageUtilities } from '@/library/message/useMessageUtilities';
import { logger as globalLogger } from '@/common/api/logger';
import {
  captureEventException,
  ErrorTrackingFlow,
  ErrorTrackingEvent,
  setChatbotContext,
} from '@/common/error-tracking';
import {
  buildConnectToAgentWithArticleConfig,
  createMessageHistory,
  createSystemMessage,
  createSystemMessageFromAI,
  createUserMessageForAI,
  createTransactionSelectMessage,
  EventType,
  getRandomInt,
  ItemType,
  mergeConnectConfig,
  randomId,
  createMediaMessage,
  checkAiBetABTestingPassed,
  createSelectedTransactionMessage,
  createUserSystemMessage,
} from '@/library/message/lib';
import type {
  ArticleInteractionExternEvent,
  ArticleSystemOption,
  BaseEvent,
  BaseExternalEvent,
  BaseMessage,
  ConnectToAgent,
  GuestLoginEvent,
  ResolutionEvent,
  UserFileForAIEvent,
  UserLoginEvent,
  UserMessageForAI,
  UserMessageForAIEvent,
  UserSelectedTransactionListEvent,
  ResumeAIChatEvent,
  PreSupportSelectionEvent,
  StartSelectedEntityFlowEvent,
  StartKYCFlowEvent,
  SystemOption,
} from '@/library/message/types';
import { checkAiABTestingPassed, checkAiTransactionABTestingPassed } from '@/common/abTesting';
import { PleasedApi } from '@/pleased-sdk';
import * as Model from '@/common/api/models.pleased';
import { ExternalBotEvent, MemberType, MessageType, ResolutionType } from '@/library/message/enum';
import { getExternalTransactionTypes, getAiRequestTransactionId, TRANSACTION_TYPE_NAME_ENUM } from '@/common/tx-utils';
import { useBotUser } from '@/stores/useBotUser';
import { AI_EVENTS, useEventLogger } from '@/composables/useEventLogger';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { devLog } from '@/common/debug';

import {
  buildAiWelcomeMessage,
  getAiGuestFallbackConfig,
  getAiGuestStartFlowConfig,
  getAiHardcodedConnectConfig,
  getInternalEventConnectConfig,
  isUserEligibleForAiFlow,
  setRecentlyConnectedAI,
} from './useAiFlowEventHandlers.utils';
import * as env from '@/common/environment';
import { isAiEligibleExternalEvent } from '@/common/bot/flow-frameworks/ai-service/AiService.eventUtils';
import { extractSelectedBetEvents } from '@/common/bot/flow-frameworks/ai-service/AiService.betEvents';
import type {
  GetAiResponseParams,
  HandleAiResponseActionParams,
  HandleAiResponseErrorParams,
  HandleAiResponseParams,
} from './useAiFlowEventHandlers.types';
import {
  isArticleFromListNavigationEvent,
  isArticleOptionEvent,
  isArticleSupportRequestEvent,
  isConnectPreSupportSelectionEvent,
  isIssueNotListedArticlesEvent,
  isOverlongResolutionEvent,
  isStaleConversationResolutionEvent,
  isUserLoginFromAIEvent,
} from './useAiFlowEventHandlers.eventUtils';
import { generateUpdatedEvent, getUserEventMessages } from '@/common/bot/flow-frameworks/ChatFramework.utils';
import { getAiChatQueueMessages } from '@/library/message/lib';
import { WatchdogUtil } from '@/common/bot/flow-frameworks/WatchdogUtil';
import { parseErrorMessage } from '@/common/error';
import { GENERAL_EVENTS } from '@/common/logs/events';
import * as HostPleasedApi from '@/common/api/api.pleased';
import { getAiActionSelectorTypes, isAiActionShowSelector } from './useAiFlowEventHandlers.static';
import { storeToRefs } from 'pinia';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { useConfig } from '@/stores/useConfig';
import { TransactionType } from '@/common/config';
import { TransStatement } from '@/common/api/model';
import {
  loadSelectedTransactionsFromStorage,
  saveRawTransactionsToStorage,
  saveSelectedTransactionsToStorage,
} from '@/common/selectedTransactions.utils';
import type { ResumeAIChat, ResumeAIChatMsg } from '@/common/types';
import { useResumeAiChat } from './useResumeAiChat';
import { ChatTriggerMatchSource } from '@/common/bot/flow-frameworks/chat-trigger';
import { useDialogTrigger } from '@/stores/useDialogTrigger';
import { useInterveneChat } from './useInterveneChat';
import { isStartKYCFlowEvent, isStartSelectedEntityFlowEvent } from '@/common/bot/lib/EventType';
import { resolutionOptionsText } from '@/components/LiveChat/Message/Resolution/resolutionConfig';

const AI_MESSAGE_LIMIT = 8;
const AI_END_CONVERSATION_DELAY = 5000;
const METADATA_MESSAGE_PREFIX = '[METADATA] ';

// AI TODO: separate types
export function useAiFlowEventHandlers({
  conversation,
  connectToAgent,
  connectArticleWithAcknowledgement,
}: {
  conversation: MessageUtilities;
  connectToAgent: (
    config?: ConnectToAgent | null,
    selectionNames?: {
      category?: string;
      sectionName?: string;
    }
  ) => void;
  connectArticleWithAcknowledgement: ({
    history,
    supportOptions,
    event,
  }: {
    supportOptions?: ConnectToAgent | null;
    history?: BaseMessage['history'];
    event?: BaseEvent | BaseExternalEvent;
  }) => void;
}) {
  const botUserStore = useBotUser();
  const { user, guestFilledDetails, guestUser, isAuthenticated } = storeToRefs(botUserStore);

  const { combineTriggerCheck, runAfterOverlayTriggerIfContinue } = useDialogTrigger();
  const logger = useEventLogger();
  const resumeAiChat = useResumeAiChat();
  const interveneChat = useInterveneChat();
  const brandConfigStore = useBrandConfig();
  const { brandConfig: brandConfigDetail } = storeToRefs(brandConfigStore);
  const brandConfig = computed(() => brandConfigDetail.value?.brandConfig);

  const connectConfigStore = useConnectConfig();
  const { setConnectConfig, updateConnectConfig } = connectConfigStore;
  const { connectConfig, isConfigSet, canConnectToAgent } = storeToRefs(connectConfigStore);

  const { config } = storeToRefs(useConfig());

  const hardcodedConnectConfig = getAiHardcodedConnectConfig();
  const defaultAiState = {
    active: false,
    enabledPerSession: true,
    isEligible: false,
    sessionId: null as string | null,
    userInputDisabled: false,
    userFileUploadDisabled: true,
    showEndChat: false,
    /** One-shot flag set when the AI session is started from KYC. Consumed on the first AI request, then reset. */
    inKycFlow: false,
  };

  const aiFlow = ref({ ...defaultAiState });

  const stopFeedbackTimer = ref<(() => void) | null>(null);
  const aiMessageCounter = ref(0);
  const currentFirebaseToken = ref<string | null>(null);
  const endChatTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
  /** Latest in-flight AI chat request; older responses are discarded (mirrors {@link AiService}). */
  const currentRequestId = ref<string | null>(null);
  /**
   * Welcome text rendered on AI entry ({@link connectToAiWithWelcome}); sent as `welcomeMessage`
   * on the first real request and cleared afterwards. Mirrors legacy `AiService.getInitialAiMessage`.
   */
  const welcomeMessageText = ref<string | null>(null);

  const resetAiMessageCounter = () => {
    aiMessageCounter.value = 0;
  };

  const postAiCommunicationMessages = (params: {
    connectConfig?: ConnectToAgent;
    messages: Array<{ message: string; author: 'AI' | 'USER' }>;
  }) => {
    const { connectConfig: connectConfigParam, messages } = params;
    const userId = isAuthenticated.value ? `${user.value.id}` : `${guestUser.value?.id ?? ''}`;
    const sessionId = connectConfigParam?.resumeAiSessionId || logger.getSessionId() || '';

    HostPleasedApi.postAiMessages({
      user_id: Number(userId),
      session_id: sessionId,
      messages,
    }).catch(() => {});
  };

  /** Original connect-to-AI event when guest was redirected to login/guest; replayed after GUEST_LOGIN / USER_LOGIN. */
  const pendingAiConnectEvent = ref<BaseEvent | BaseExternalEvent | null>(null);

  /** Guest AI flow: AI returned a transaction-selector action; we defer messages until the visitor signs in. */
  const pendingGuestAiResponseAction = ref<Model.AiResponseAction | null>(null);
  const pendingGuestAiMessages = ref<BaseMessage[]>([]);

  const aiFlowStartedHook = createEventHook<void>();
  const interventionHook = createEventHook<{ chatId: number; firebaseToken: string }>();
  const endAiChatHook = createEventHook<void>();

  const triggerEndAiChat = () => {
    aiFlow.value.showEndChat = true;
    endAiChatHook.trigger();
  };

  function restartAiFlow() {
    stopFeedbackTimer.value?.();
    resumeAiChat.markSessionAsEnded(aiFlow.value.sessionId);
    aiFlow.value = { ...defaultAiState };
    pendingAiConnectEvent.value = null;
    pendingGuestAiResponseAction.value = null;
    pendingGuestAiMessages.value = [];
    resetAiMessageCounter();
    currentRequestId.value = null;
    welcomeMessageText.value = null;
  }

  const checkIsGuestTxAction = (action: Model.AiResponseAction | null | undefined): boolean => {
    if (!action) return false;
    return Boolean(user.value.isGuest && isAiActionShowSelector(action));
  };

  /**
   * Deferred AI lines were created when the API returned; their timestamps sit in the past.
   * On login, re-stamp with now + index so {@link sortMessageItems} keeps order within the batch
   * and places them after messages added during the login flow.
   */
  const withDeferredGuestAiMessageTimestamps = (messages: BaseMessage[]): BaseMessage[] => {
    const baseMs = Date.now();
    return messages.map((message, index) => ({
      ...message,
      timestamp: baseMs + index,
    }));
  };

  function disableUserInputForAi() {
    aiFlow.value.userInputDisabled = true;
  }

  function checkCanConnectToAI(eventConfig?: ConnectToAgent) {
    const userLoyaltyTier = user.value.userLoyaltyTierBand;

    const {
      aiEnabled,
      aiAbTestingEnabled,
      abTestPercentage,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
      openAiDown,
    } = brandConfig.value || {};

    const isEligible = isUserEligibleForAiFlow({
      userId: user.value.id,
      isUserLoggedIn: !user.value.isGuest,
      userLoyaltyTier,
      openAiDown,
      aiEnabledPerBrand: aiEnabled,
      aiEnabledPerSession: aiFlow.value.enabledPerSession,
      aiAbTestingEnabled,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
      abTestPercentage: abTestPercentage,
      skipAiChat: eventConfig?.skipAiChat || false,
      enableMetadataRules: eventConfig?.enableMetadataRules || false,
    });

    aiFlow.value.isEligible = isEligible;
    return isEligible;
  }

  function checkEventCanConnectToAI(event: BaseEvent | BaseExternalEvent) {
    const isExternalEvent = EventType.isExternalEvent(event);
    const eventConfig = (
      isExternalEvent ? event.payload?.supportOptions : getInternalEventConnectConfig(event as BaseEvent)
    ) as ConnectToAgent;
    return checkCanConnectToAI(eventConfig ?? connectConfig.value);
  }

  /**
   * When AI is eligible but the visitor has not logged in or completed guest identity,
   * show the login/guest system line and open the connect wizard (mirrors legacy {@link AiService.connectToAI}).
   */
  function tryRedirectGuestBeforeAi(event: BaseEvent | BaseExternalEvent): boolean {
    if (isAuthenticated.value || guestFilledDetails.value) {
      return false;
    }

    const isExternalEvent = EventType.isExternalEvent(event);
    const eventConnectConfig = (
      isExternalEvent ? event.payload?.supportOptions : getInternalEventConnectConfig(event as BaseEvent)
    ) as ConnectToAgent | undefined;

    const guestFlowConfig = getAiGuestStartFlowConfig(eventConnectConfig);
    setConnectConfig(guestFlowConfig);

    logger.trackAiEvent({
      name: AI_EVENTS.AI_RUN_CONNECT_TO_AI,
      metadata: {
        connectConfig: eventConnectConfig,
        isUserLoggedIn: !user.value.isGuest,
        isGuestFilledDetails: guestFilledDetails.value,
      },
    });

    const loginCopy = guestFlowConfig.loginRequired
      ? (tConversation('chat.sys.login.required') as string)
      : (tConversation('chat.sys.login.optional') as string);

    conversation.addItems(
      createSystemMessageFromAI({
        message: loginCopy,
        sessionId: logger.getSessionId() || '',
      })
    );

    pendingAiConnectEvent.value = deepClone(event);

    connectToAgent(guestFlowConfig);
    return true;
  }

  function startAiSession(sessionId: string = logger.getSessionId() || '') {
    // Idempotent: the session is started on AI entry ({@link connectToAiWithWelcome}); the first
    // typed message must not re-trigger the start hook or overwrite the session id.
    if (aiFlow.value.active) return;

    logger.trackAiEvent({
      name: AI_EVENTS.AI_SESSION_START,
    });

    setChatbotContext({ flow: ErrorTrackingFlow.Ai });
    aiFlow.value.active = true;
    aiFlow.value.sessionId = sessionId;
    aiFlowStartedHook.trigger();
  }

  /**
   * Builds the fixed AI welcome message shown on entry, mirroring legacy {@link AiService.connectToAI}:
   * the brand-config `aiWelcomeMessage` (or the `chat.msg.ai.welcome_default` i18n fallback), with the
   * requester name, category and app-name placeholders replaced. `aiMessageOverride` lets a specific
   * entry point supply its own text (e.g. the selected-entity flow).
   */
  function buildWelcomeMessage(aiMessageOverride?: string | null): string {
    const category = connectConfig.value?.category || '';
    const appName = env.get('appName') || '';
    const requesterName = user.value?.name || '';

    const fallbackMessage = tConversation('chat.msg.ai.welcome_default', {
      appName,
      categoryName: category,
      requesterName,
    }) as string;

    return buildAiWelcomeMessage({
      aiWelcomeMessage: brandConfig.value?.aiWelcomeMessage,
      fallbackMessage,
      override: aiMessageOverride,
      category,
      requesterName,
      appName,
    });
  }

  /**
   * AI entry step, mirroring legacy {@link AiService.connectToAI}: shows the user's selection, renders
   * the fixed welcome message as the first AI-side reply, starts the session and marks the connect
   * config — WITHOUT hitting the live AI API. The first real request is deferred to the user's first
   * typed message ({@link handleMessageFromUserForAI}), which forwards {@link welcomeMessageText}.
   *
   * @returns `false` when the visitor was redirected to login/guest (no welcome shown); `true` otherwise.
   */
  function connectToAiWithWelcome(params: {
    event: BaseEvent | BaseExternalEvent;
    entryText?: string | null;
    aiMessageOverride?: string | null;
  }): boolean {
    const { event, entryText, aiMessageOverride } = params;

    // Guest guard (same as legacy connectToAI): if the visitor still needs to log in / fill guest
    // details, show the login line and open the connect wizard; the entry replays after login.
    if (tryRedirectGuestBeforeAi(event)) {
      return false;
    }

    // Keep the user's selection/entry text visible on the user side. Rendered as a user system
    // message (not a UserMessageForAI) so it does not count as the first request in
    // {@link checkIsFirstRequestToAI} — matching legacy, where entry never adds a UserMessageForAI.
    if (entryText) {
      conversation.addItems(
        createUserSystemMessage({
          message: entryText,
        })
      );
    }

    const welcomeMessage = buildWelcomeMessage(aiMessageOverride);
    welcomeMessageText.value = welcomeMessage;

    conversation.addItems(
      createSystemMessageFromAI({
        message: welcomeMessage,
        sessionId: logger.getSessionId() || '',
      })
    );

    markAiConversation();
    startAiSession();

    return true;
  }

  async function getAiResponse(params: GetAiResponseParams) {
    const { message, isFirstRequest, welcomeMessage, metadata } = params;

    const {
      aiAbTestingEnabled,
      abTestPercentage,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiBetEnabled,
      aiBetAbRatio,
    } = brandConfig.value || {};

    const aiAbTestPassed = checkAiABTestingPassed({
      userId: user.value.id,
      enableAbTesting: aiAbTestingEnabled,
      abValue: abTestPercentage,
    });

    const aiTransactionAbTestPassed = checkAiTransactionABTestingPassed({
      userId: user.value.id,
      enableAbTesting: aiTransactionEnabled,
      abValue: aiTransactionAbRatio,
      aiAbTestPassed,
    });

    const aiBetAbTestPassed = checkAiBetABTestingPassed({
      userId: user.value.id,
      enableAbTesting: aiBetEnabled,
      abValue: aiBetAbRatio,
      aiAbTestPassed,
    });

    const sessionId = connectConfig.value?.resumeAiSessionId || logger.getSessionId() || '';

    const inKycFlow = isFirstRequest && aiFlow.value.inKycFlow;
    if (inKycFlow) {
      aiFlow.value.inKycFlow = false;
    }

    const selectedBetEvents = extractSelectedBetEvents(metadata);
    const userSelections = getAiChatQueueMessages(conversation.messages.value);

    const requestParams = {
      user_id: isAuthenticated.value ? `${user.value.id}` : `${guestUser.value?.id}`,
      user_input: message,
      session_id: sessionId,
      first_request: isFirstRequest,
      transaction_enable: aiTransactionAbTestPassed,
      bet_enable: aiBetAbTestPassed,
      ...(selectedBetEvents ? { betEvents: selectedBetEvents } : {}),
      ...(isFirstRequest
        ? {
            userLogs: reportingService.getLogs(),
            userSelections,
            welcomeMessage,
            in_kyc_flow: inKycFlow,
          }
        : {}),
      metadata: isFirstRequest ? { __USER_SELECTIONS: userSelections, ...metadata } : metadata,
    };

    logger.trackAiEvent({
      name: AI_EVENTS.AI_USER_REQUEST,
      metadata: { ...requestParams },
    });

    if (isAuthenticated.value) {
      return PleasedApi.getAiChatResponse(requestParams);
    }

    return PleasedApi.getAiChatResponseGuest(requestParams);
  }

  /*
   * Marks as AI conversation in connect config, needed for correct tags and flow.
   * Should be included to all user messages flows with AI:
   * - User message for AI
   * - User transaction metadata
   * - AI response (in case AI starts conversation)
   * - File upload for AI
   * - Login from AI suggestion
   */
  function markAiConversation() {
    updateConnectConfig({ aiConversation: true });
  }

  async function handleDetectedConversationLanguage(aiResponse: Model.AiChatResponsePayload) {
    const detectedLanguage = aiResponse?.detectedLanguage;
    if (!detectedLanguage) return;
    if (!isSupported(detectedLanguage)) return;
    await setConversationLocaleOverride(detectedLanguage as any);
  }

  function handleAiResponseAction(params: Omit<HandleAiResponseActionParams, 'event'>) {
    const { action, metadata } = params;

    if (!action) return;

    const actionSwitcher = switcher<AiResponseAction>()
      .when(AiResponseAction.CONNECT_TO_AGENT, () => {
        logger.trackAiEvent({
          name: AI_EVENTS.AI_CONNECT_SUPPORT_RESPONSE,
          metadata,
        });

        // If user is not able to connect to agent, we set the hardcoded config
        if (!canConnectToAgent.value) {
          updateConnectConfig(hardcodedConnectConfig);
        }

        // For AI flow, we don't want to show description box for happy flow transition to Agent
        updateConnectConfig({ showDescriptionBox: false });

        connectToAgentFromAiFlow();
      })
      .when(AiResponseAction.END_CHAT, () => {
        logger.trackAiEvent({
          name: AI_EVENTS.AI_END_CHAT_RESPONSE,
          metadata,
        });

        triggerEndAiChat();
        finishAiFlowPerSession();
      })
      .when(AiResponseAction.AI_END_CONVERSATION, () => {
        logger.trackAiEvent({
          name: AI_EVENTS.AI_END_CONVERSATION,
          metadata,
        });

        finishAiFlowPerSession();
        disableUserInputForAi();

        if (endChatTimeout.value) {
          clearTimeout(endChatTimeout.value);
          endChatTimeout.value = null;
        }

        endChatTimeout.value = setTimeout(() => {
          triggerEndAiChat();
          endChatTimeout.value = null;
        }, AI_END_CONVERSATION_DELAY);
      })
      .when(AiResponseAction.TICKET_END_CHAT, () => {
        logger.trackAiEvent({
          name: AI_EVENTS.AI_TICKET_END_CHAT_RESPONSE,
          metadata,
        });

        triggerEndAiChat();
        finishAiFlowPerSession();
      })
      .when(isAiActionShowSelector, (action) => {
        aiFlow.value.userInputDisabled = true;

        logger.trackAiEvent({
          name: AI_EVENTS.AI_SHOW_TRANSACTION_SELECTOR,
          metadata,
        });

        const transactionTypes = getAiActionSelectorTypes(action);
        const transactionSubtypes = Array.isArray(transactionTypes.status)
          ? transactionTypes.status
          : [transactionTypes.status];

        conversation.addItems(
          createTransactionSelectMessage({
            config: { connect: connectConfig.value },
            txSubtypes: transactionSubtypes,
            singleSelectOnly: true,
          })
        );
      })
      .when(AiResponseAction.COLLECT_FILE, () => {
        logger.trackAiEvent({
          name: AI_EVENTS.AI_COLLECT_FILE,
          metadata,
        });

        aiFlow.value.userFileUploadDisabled = false;
      })
      .none(() => {
        globalLogger.error('[AI_FLOW_EVENT_HANDLERS] Unhandled action', { action });
      });

    actionSwitcher.match(action);
  }

  // AI TODO: add support
  // const handleRateAiResponseEvent = (event: RateAiResponseEvent) => {
  //   const {
  //     message,
  //     payload: { selected },
  //   } = event;
  // chatFramework.emitter?.emit('rateAiResponse', { message, rate: selected });
  // };

  const handleOverlongAiConversationResolution = (event: ResolutionEvent) => {
    const { message } = event;
    const { selected } = event.payload;

    aiFlow.value.userInputDisabled = false;

    const optionText = resolutionOptionsText(message.type)[selected] || selected;

    postAiCommunicationMessages({
      connectConfig: message.config?.connect as ConnectToAgent,
      messages: [{ message: optionText, author: 'USER' }],
    });

    conversation.addItems(
      createUserSystemMessage({
        message: optionText,
        history: createMessageHistory(event, message),
      })
    );

    if (selected === ResolutionType.YES) {
      logger.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONNECT_SUPPORT,
        metadata: { message: message.message },
      });

      finishAiFlowPerSession();
      connectArticleWithAcknowledgement({
        supportOptions: message.config?.connect as ConnectToAgent,
        event,
        history: createMessageHistory(event, message),
      });
    } else if (selected === ResolutionType.NO) {
      logger.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONTINUE,
        metadata: { message: message.message },
      });
    }
  };

  // const onSectionSelectOption = (event: BaseEvent) => {
  //   const selected = { ...event.payload.selected, showDescriptionBox: true };
  //   $pr.selectedSection(selected.id);

  //   connectToAI(generateUpdatedEvent(event, selected));
  // };

  const onArticleSelectOption = async (event: BaseEvent) => {
    // First check for Chat Triggers (to not connect to AI in case of match)
    await runAfterOverlayTriggerIfContinue(
      {
        type: ChatTriggerMatchSource.ARTICLE,
        id: event.payload.selected.id,
      },
      async () => {
        // Then check for Guest flow before connecting to AI
        if (tryRedirectGuestBeforeAi(event)) {
          return;
        }
        if (!event.message?.config) return;
        const selected = event.payload.selected as ArticleSystemOption;
        $pr.selectedArticle(selected.id);

        const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(event.message.config.connect!, selected);
        setConnectConfig(parentWithArticleConfig);

        connectToAiWithWelcome({ event, entryText: selected.title });
      }
    );
  };

  const onIssueNotListedArticles = (event: BaseEvent) => {
    const updatedEvent = generateUpdatedEvent(event, { showDescriptionBox: true });
    setConnectConfig(updatedEvent.message.config?.connect!);
    connectToAiWithWelcome({
      event: updatedEvent,
      entryText: tConversation('chat.sys.msg.ai.issue_not_listed_help') as string,
    });
  };

  function handleAiFlowFromArticle(event: ArticleInteractionExternEvent) {
    setConnectConfig(event.payload.supportOptions);
    connectToAiWithWelcome({ event, entryText: event.payload.article.title });
  }

  function checkIsFirstRequestToAI(): boolean {
    const messagesFromUser = conversation.messages.value.filter((m) => ItemType.isUserMessageForAI(m));
    return messagesFromUser.length === 0;
  }

  function submitUserMessageForAI(message: string) {
    const event: UserMessageForAIEvent = {
      type: ExternalBotEvent.USER_MESSAGE_FOR_AI,
      id: randomId(),
      payload: {
        message,
      },
    };

    submitAiEvent(event);
  }

  const handleGuestLoginExternalEvent = (event: GuestLoginEvent) => {
    runGuestLoginAiFlowContinuation(event);
  };

  const handleUserLoginExternalEvent = (event: UserLoginEvent) => {
    runUserLoginAiFlowContinuation(event);
  };

  const runGuestLoginAiFlowContinuation = async (event: GuestLoginEvent) => {
    const guest = guestUser.value;
    const visitorInfo = {
      displayName: guest?.defaultName || guest?.name || '',
      email: guest?.email || '',
      name: guest?.name || '',
      phone: guest?.phone || '',
      // isGuest: ,
    };
    const description = guest?.description || '';

    logger.track({
      name: GENERAL_EVENTS.GUEST_FILL_DETAILS,
      metadata: {
        visitorInfo: { visitorInfo, description },
      },
    });

    logger.track({
      name: GENERAL_EVENTS.SAVE_GUEST_INFO,
      metadata: {
        visitorInfo: { visitorInfo, description },
      },
    });

    await HostPleasedApi.saveGuestUserInfo({
      email: visitorInfo.email,
      phone: visitorInfo.phone,
      name: visitorInfo.name || visitorInfo.displayName,
    }).catch((err) => {
      logger.track({
        name: GENERAL_EVENTS.SAVE_GUEST_INFO_FAILED,
        metadata: {
          error: parseErrorMessage(err),
        },
      });
    });

    logger.trackAiEvent({ name: AI_EVENTS.AI_GUEST_LOGIN });
    replayPendingAiConnectEvent();
  };

  const runUserLoginAiFlowContinuation = async (_event: UserLoginEvent) => {
    logger.track({
      name: GENERAL_EVENTS.GUEST_LOGIN_AI_SUGGESTED,
    });

    const guestToken = guestUser.value?.token;
    const sessionId = logger.getSessionId();
    const userToken = user.value.token;

    if (guestToken && sessionId && userToken) {
      await HostPleasedApi.saveGuestLoginToken({
        sessionId,
        userToken,
        guestToken,
      }).catch((err) => {
        logger.track({
          name: GENERAL_EVENTS.SAVE_GUEST_LOGIN_TOKEN_FAILED,
          metadata: {
            error: parseErrorMessage(err),
          },
        });
      });
    }

    logger.trackAiEvent({ name: AI_EVENTS.AI_USER_LOGIN });

    if (pendingGuestAiMessages.value.length > 0) {
      conversation.addItems(...withDeferredGuestAiMessageTimestamps(pendingGuestAiMessages.value));
      pendingGuestAiMessages.value = [];
    }

    const deferredGuestTxAction = pendingGuestAiResponseAction.value;
    if (deferredGuestTxAction) {
      pendingGuestAiResponseAction.value = null;
      handleAiResponseAction({
        action: deferredGuestTxAction,
        metadata: { userMessage: null, aiResponse: { action: deferredGuestTxAction } },
      });
    }

    replayPendingAiConnectEvent();
  };

  /*
   * Handles user message for AI.
   * If user types message for AI with no config set, we set the hardcoded config
   * @param message - The message from the user
   *
   */
  function handleUserMessageForAI(message: string) {
    const userMessage = createUserMessageForAI({
      message,
      sessionId: logger.getSessionId() || '',
    }) as UserMessageForAI;

    aiMessageCounter.value += 1;
    conversation.addItems(userMessage);

    if (!isConfigSet.value) {
      setConnectConfig(hardcodedConnectConfig);
    }

    markAiConversation();

    logger.trackAiEvent({
      name: AI_EVENTS.AI_USER_INPUT,
      selection: userMessage.id,
      metadata: { message: userMessage.message, messageId: userMessage.id },
    });

    return userMessage;
  }

  async function handleFirstResponse(params: { aiResponse: Model.AiChatResponsePayload; isFirstRequest: boolean }) {
    const { aiResponse, isFirstRequest } = params;

    if (!aiResponse) return null;

    await handleDetectedConversationLanguage(aiResponse);

    const aiResponseMessage = createSystemMessageFromAI({
      message: aiResponse.response[0],
      sessionId: logger.getSessionId() || '',
      aiResponse,
    });
    logger.trackAiEvent({
      name: AI_EVENTS.AI_RESPONSE_FIRST,
      selection: aiResponseMessage.id,
      metadata: {
        message: aiResponseMessage.message,
        messageId: aiResponseMessage.id,
        aiResponse,
      },
    });

    if (isFirstRequest) {
      const aiUserId = isAuthenticated.value ? user.value.id : guestUser.value?.id;
      setRecentlyConnectedAI(aiUserId);

      // Setup Firebase intervention listener for AI chat (members and guests, same as AiService.handleAiChatCreation)
      if (aiResponse.firebaseToken) {
        currentFirebaseToken.value = aiResponse.firebaseToken;

        const accessToken = isAuthenticated.value ? user.value.token : guestUser.value?.token;

        interveneChat.setupFirebaseAiChat({
          firebaseToken: aiResponse.firebaseToken,
          connectConfig: connectConfig.value!,
          user: {
            userId: aiUserId,
            accessToken,
          },
          onIntervene: handleAiChatIntervention,
        });
      }
    }

    if (checkIsGuestTxAction(aiResponse.action)) {
      pendingGuestAiResponseAction.value = aiResponse.action;
      pendingGuestAiMessages.value.push(aiResponseMessage);
      disableUserInputForAi();

      const loginCopy =
        (brandConfig.value?.aiGuestUsersMessage as string | null | undefined) ||
        (tConversation('chat.sys.login.required') as string);

      return createSystemMessage({
        type: MessageType.USER_LOGIN_FROM_AI,
        message: loginCopy,
      });
    }

    return aiResponseMessage;
  }

  async function handleFollowUpResponses(aiResponse: Model.AiChatResponsePayload) {
    if (!aiResponse || aiResponse.response.length <= 1) return aiResponse;

    await handleDetectedConversationLanguage(aiResponse);

    const deferForGuestLogin = checkIsGuestTxAction(aiResponse.action);

    for (let i = 1; i < aiResponse.response.length; i++) {
      const aiResponseMessage = createSystemMessageFromAI({
        message: aiResponse.response[i],
        sessionId: logger.getSessionId() || '',
        aiResponse,
      });

      if (deferForGuestLogin) {
        pendingGuestAiMessages.value.push(aiResponseMessage);
        continue;
      }

      const delayPromise = waitFor(getRandomInt(1, 4) * 1000);
      await conversation.enqueue(
        delayPromise.then(() => {
          logger.trackAiEvent({
            name: AI_EVENTS.AI_RESPONSE_FOLLOW_UP,
            selection: aiResponseMessage.id,
            metadata: {
              messageIndex: i,
              message: aiResponseMessage.message,
              messageId: aiResponseMessage.id,
              aiResponse,
            },
          });
          return aiResponseMessage;
        })
      );
    }
    return aiResponse;
  }

  function handleOverlongConversation(aiResponse: Model.AiChatResponsePayload): boolean | undefined {
    if (!aiResponse) return;
    // Skip overlong conversation if AI response has action (same as AiService)
    if (aiResponse.action) return;

    aiMessageCounter.value += aiResponse.response.length;
    if (aiMessageCounter.value < AI_MESSAGE_LIMIT) return;

    resetAiMessageCounter();

    // If user is not able to connect to agent, we set the hardcoded config
    if (!canConnectToAgent.value) {
      updateConnectConfig(hardcodedConnectConfig);
    }

    conversation.addItems(
      createSystemMessage({
        type: MessageType.OVERLONG_AI_CONVERSATION_RESOLUTION,
        message: tConversation('chat.sys.msg.ai.overlong_conversation') as string,
        config: { connect: connectConfig.value },
      })
    );

    postAiCommunicationMessages({
      connectConfig: connectConfig.value ?? undefined,
      messages: [{ message: i18n.t('chat.sys.msg.ai.overlong_conversation') as string, author: 'AI' }],
    });

    return true;
  }

  const handleMessageFromUserForAI = async (event: UserMessageForAIEvent) => {
    const { message } = event.payload;

    stopFeedbackTimer.value?.();
    disableUserInputForAi();

    let isOverlongConversation = false;
    let requestId: string | undefined;
    try {
      const isFirstRequest = checkIsFirstRequestToAI();

      // If it's first message, start AI session (no-op if AI entry already started it)
      if (isFirstRequest) {
        startAiSession();
      }

      handleUserMessageForAI(message);

      // On the first real request, forward the welcome text rendered on entry (mirrors legacy
      // AiService.handleMessageFromUserForAI passing getInitialAiMessage().message).
      const welcomeMessage = isFirstRequest ? (welcomeMessageText.value ?? undefined) : undefined;

      const aiResponsePromise = getAiResponse({
        message,
        isFirstRequest,
        welcomeMessage,
      });

      if (isFirstRequest) {
        welcomeMessageText.value = null;
      }

      requestId = randomId();
      currentRequestId.value = requestId;

      isOverlongConversation = await handleAiResponse({
        userMessage: message,
        isFirstRequest,
        aiResponsePromise,
        requestId,
      });
    } finally {
      if (
        requestId !== undefined &&
        requestId === currentRequestId.value &&
        !pendingGuestAiResponseAction.value &&
        !isOverlongConversation
      ) {
        aiFlow.value.userInputDisabled = false;
      }
    }
  };

  const handleUserTransactionMetadata = async (event: UserSelectedTransactionListEvent) => {
    const { transactionType, transactions } = event.payload;

    const transactionTypeName = TRANSACTION_TYPE_NAME_ENUM[transactionType];
    const isTransactionSelected = transactions.length > 0;
    const selectedTransaction = transactions[0] as ExtendedSelectedTransStatement;
    const isBetTransaction = transactionType === TransactionType.Bet;

    const message = (
      isTransactionSelected
        ? tConversation('chat.sys.msg.ai.transaction_help', {
            transactionType: transactionTypeName,
            transactionId: getAiRequestTransactionId(selectedTransaction),
            userEvents:
              selectedTransaction?.__USER_EVENTS?.length > 0
                ? getUserEventMessages(selectedTransaction.__USER_EVENTS)
                : '',
          })
        : tConversation('chat.sys.msg.ai.transaction_not_listed_help')
    ) as string;

    const isFirstRequest = checkIsFirstRequestToAI();

    aiMessageCounter.value += 1;
    markAiConversation();
    disableUserInputForAi();

    if (isTransactionSelected) {
      await conversation.enqueue(
        combineTriggerCheck({
          into: Promise.resolve(
            createSelectedTransactionMessage({
              transactions,
              transactionType,
              message,
              isEditable: false,
              fromAiFlow: true,
            })
          ),
          type: 'TRANSACTION',
          transactionType: transactionTypeName,
          transaction: selectedTransaction,
        })
      );
    } else {
      conversation.addItems(
        createUserSystemMessage({
          message,
        })
      );
    }

    // Currently we'll add all necessary data for AI layer here
    const metadata = isTransactionSelected
      ? {
          // Payment transactions (D+W) should include "Metadata" from external API
          // Currently fetched before submitting transaction
          ...selectedTransaction,
          transactionNotSelected: false,

          // All transactions should include SELECTED_ENTITY_TYPE with type in uppercase
          SELECTED_ENTITY_TYPE: TRANSACTION_TYPE_NAME_ENUM[transactionType].toUpperCase(),

          // Bets should include __USER_EVENTS and __VALIDATED_FOREIGN_ID
          ...(isBetTransaction && {
            __USER_EVENTS: selectedTransaction.__USER_EVENTS,
            // Same source as the legacy flow's initForeignId: the host INIT userId.
            __VALIDATED_FOREIGN_ID: config.value?.userId,
          }),
        }
      : { transactionNotSelected: true };

    // We save selected transactions to localStorage for persistence with Resume AI feature
    if (isTransactionSelected) {
      const selectedTransactionsData = saveRawTransactionsToStorage(transactionType, transactions);
      updateConnectConfig({ selectedTransactionsData });
    }

    let isOverlongConversation = false;
    let requestId: string | undefined;
    try {
      const aiResponsePromise = getAiResponse({
        message: `${METADATA_MESSAGE_PREFIX}${message}`,
        isFirstRequest,
        metadata,
      });

      requestId = randomId();
      currentRequestId.value = requestId;

      isOverlongConversation = await handleAiResponse({
        userMessage: null,
        isFirstRequest,
        aiResponsePromise,
        requestId,
      });
    } finally {
      if (
        requestId !== undefined &&
        requestId === currentRequestId.value &&
        !pendingGuestAiResponseAction.value &&
        !isOverlongConversation
      ) {
        aiFlow.value.userInputDisabled = false;
      }
    }
  };

  const handleFileFromUserForAI = async (event: UserFileForAIEvent) => {
    const { fileUrl, fileName, contentType, attachmentId } = event.payload;

    const message = tConversation('chat.sys.msg.ai.file_help') as string;
    const isFirstRequest = checkIsFirstRequestToAI();

    conversation.addItems(
      createUserMessageForAI({
        message,
        sessionId: logger.getSessionId() || '',
      }),
      createMediaMessage({
        message,
        attachment: {
          id: attachmentId,
          url: fileUrl,
          mimeType: contentType,
          contentType,
          name: fileName,
        },
        memberType: MemberType.VISITOR,
      })
    );

    markAiConversation();
    disableUserInputForAi();
    // File Input available only after action from AI for 1 message from user.
    aiFlow.value.userFileUploadDisabled = true;

    let isOverlongConversation = false;
    let requestId: string | undefined;
    try {
      const aiResponsePromise = getAiResponse({
        message: `${METADATA_MESSAGE_PREFIX}${message}`,
        isFirstRequest,
        metadata: { fileUrl, attachmentId },
      });

      requestId = randomId();
      currentRequestId.value = requestId;

      isOverlongConversation = await handleAiResponse({
        userMessage: null,
        isFirstRequest,
        aiResponsePromise,
        requestId,
      });
    } finally {
      if (
        requestId !== undefined &&
        requestId === currentRequestId.value &&
        !pendingGuestAiResponseAction.value &&
        !isOverlongConversation
      ) {
        aiFlow.value.userInputDisabled = false;
      }
    }
  };

  async function handleAiResponse(params: HandleAiResponseParams): Promise<boolean> {
    const { isFirstRequest, userMessage, aiResponsePromise, requestId } = params;

    if (requestId !== undefined && requestId !== currentRequestId.value) {
      return false;
    }

    try {
      const messagePromise = conversation.enqueue(
        aiResponsePromise.then((aiResponse) => handleFirstResponse({ aiResponse, isFirstRequest }))
      );

      const [aiResponse] = await Promise.all([aiResponsePromise, messagePromise]);

      stopFeedbackTimer.value?.();
      startTimedFeedback();

      await handleFollowUpResponses(aiResponse);

      const guestTxDeferred = checkIsGuestTxAction(aiResponse.action);
      if (!guestTxDeferred) {
        handleAiResponseAction({
          action: aiResponse.action,
          metadata: { userMessage, aiResponse },
        });
        const isOverlongConversation = handleOverlongConversation(aiResponse);
        if (isOverlongConversation) {
          return true;
        }
      }

      return false;
    } catch (error) {
      globalLogger.error('[AI_FLOW_EVENT_HANDLERS] Error in AI flow', { error: parseErrorMessage(error) });

      handleAiResponseError({
        userMessage,
        error: parseErrorMessage(error),
        isFirstRequest,
      });

      return false;
    }
  }

  // TODO: remove connectConfig from params, with remove of old chatbot
  const handleAiResponseError = (params: Omit<HandleAiResponseErrorParams, 'connectConfig'>) => {
    const { userMessage, error, isFirstRequest } = params;
    globalLogger.error('[AI_FLOW_EVENT_HANDLERS] Error in AI flow', { error: parseErrorMessage(error) });
    captureEventException(error, ErrorTrackingEvent.AiResponseError);
    logger.trackAiEvent({
      name: AI_EVENTS.AI_ERROR_RESPONSE,
      metadata: { message: userMessage, error },
    });

    conversation.addItems(
      createSystemMessage({
        type: MessageType.CHAT_MESSAGE,
        message: tConversation('chat.sys.msg.ai.response_error') as string,
      })
    );

    // Update connect config with isFirstRequest flag, needed for correct tags
    updateConnectConfig({ isFirstRequest });
    connectToAgentFromAiFlow();

    return null;
  };

  function finishAiFlowPerSession() {
    aiFlow.value.active = false;
    aiFlow.value.enabledPerSession = false;
    stopFeedbackTimer.value?.();
    interveneChat.cleanup();
    if (endChatTimeout.value) {
      clearTimeout(endChatTimeout.value);
      endChatTimeout.value = null;
    }
  }

  function resetAiFlow() {
    finishAiFlowPerSession();
  }

  function connectToAgentFromAiFlow() {
    finishAiFlowPerSession();
    connectToAgent(connectConfig.value);
  }

  /**
   * Handles AI chat intervention when an agent takes over
   * This will trigger the intervention hook to be handled by the connect wizard
   */
  function handleAiChatIntervention(chatId: number) {
    logger.trackAiEvent({
      name: AI_EVENTS.AI_INTERVENE_CHAT,
      metadata: { chatId },
    });

    // End the AI flow since agent is taking over
    finishAiFlowPerSession();

    // Trigger intervention hook with chatId and firebaseToken
    if (currentFirebaseToken.value) {
      interventionHook.trigger({
        chatId,
        firebaseToken: currentFirebaseToken.value,
      });
    }
  }

  function startTimedFeedback() {
    const { enableTimedAiFeedback, timedAiFeedbackNoMessagesTimeInSeconds = 0 } = brandConfig.value
      ?.timedAiFeedbackConfig || {
      enableTimedAiFeedback: false,
      timedAiFeedbackNoMessagesTimeInSeconds: 0,
    };

    if (stopFeedbackTimer.value) return;
    if (!enableTimedAiFeedback || !timedAiFeedbackNoMessagesTimeInSeconds) return;
    const stopTimer = WatchdogUtil.startWatchdog(
      timedAiFeedbackNoMessagesTimeInSeconds * 1000,
      5000,
      handleTimedFeedback
    );
    stopFeedbackTimer.value = () => {
      stopTimer();
      stopFeedbackTimer.value = null;
    };
  }

  const handleTimedFeedback = () => {
    const { timedAiFeedbackConfirmationMessageText = '' } = brandConfig.value?.timedAiFeedbackConfig || {
      timedAiFeedbackConfirmationMessageText: '',
    };

    // If user input already disabled, don't show the message

    if (aiFlow.value.userInputDisabled) return;

    logger.trackAiEvent({
      name: AI_EVENTS.AI_STALE_CONVERSATION,
      metadata: { text: timedAiFeedbackConfirmationMessageText },
    });

    stopFeedbackTimer.value?.();
    disableUserInputForAi();

    conversation.addItems(
      createSystemMessage({
        type: MessageType.STALE_AI_CONVERSATION_RESOLUTION,
        message: timedAiFeedbackConfirmationMessageText,
      })
    );
  };

  const handleStaleAiConversation = (event: ResolutionEvent) => {
    if (event.payload.selected === ResolutionType.YES) {
      const { timedAiFeedbackResumeMessageText = '' } = brandConfig.value?.timedAiFeedbackConfig || {
        timedAiFeedbackResumeMessageText: '',
      };

      logger.trackAiEvent({
        name: AI_EVENTS.AI_STALE_CONVERSATION_RESUME,
        metadata: { text: timedAiFeedbackResumeMessageText },
      });
      aiFlow.value.userInputDisabled = false;
      conversation.addItems(
        createSystemMessage({
          type: MessageType.CHAT_MESSAGE,
          message: timedAiFeedbackResumeMessageText,
        })
      );
    } else if (event.payload.selected === ResolutionType.NO) {
      logger.trackAiEvent({ name: AI_EVENTS.AI_STALE_CONVERSATION_END });
      triggerEndAiChat();
      finishAiFlowPerSession();
    }
  };

  function updateMessageSelection(message: BaseMessage, selected: string | SystemOption) {
    const updatedMessage = { ...message, selectedOption: selected };
    conversation.addItems(updatedMessage);
  }

  const handleUserLoginFromAIResolution = (event: ResolutionEvent) => {
    const { message } = event;
    const { selected, selectedText } = event.payload;

    updateMessageSelection(message, selected);

    if (selectedText) {
      conversation.addItems(
        createUserSystemMessage({
          message: selectedText,
        })
      );
    }

    if (selected === ResolutionType.YES) {
      logger.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONNECT_SUPPORT,
        metadata: { message: message.message },
      });

      const base = connectConfig.value ?? hardcodedConnectConfig;
      const loginOnlyConfig = mergeConnectConfig(base, {
        loginRequired: true,
        aiConversation: true,
        aiMandatoryAccountLogin: true,
      });
      setConnectConfig(loginOnlyConfig);
      connectToAgent(loginOnlyConfig);
    } else if (selected === ResolutionType.NO) {
      const declineCopy = brandConfig.value?.aiGuestUsersResponseText;

      pendingGuestAiResponseAction.value = null;
      pendingGuestAiMessages.value = [];

      declineCopy &&
        conversation.addItems(
          createSystemMessageFromAI({
            message: declineCopy,
          })
        );

      finishAiFlowPerSession();
      setConnectConfig(getAiGuestFallbackConfig());
      connectToAgent(getAiGuestFallbackConfig());
    }

    aiFlow.value.userInputDisabled = false;
  };

  const handleResumeAIChat = (event: ResumeAIChatEvent) => {
    // Reset request guard so resumed conversation can process new requests (same as AiService)
    currentRequestId.value = null;

    const payload = event.payload;
    const messages = payload.messages;
    const resumeAiSessionId = payload.sessionId;
    const lastMessage = messages[messages.length - 1];

    // Load selectedTransactionsData from localStorage for resume AI functionality
    const storedSelectedTransactionsData = loadSelectedTransactionsFromStorage();

    const config: ConnectToAgent = {
      ...hardcodedConnectConfig,
      resumeAiSessionId,
      aiConversation: true,
      // Only add selectedTransactionsData if it exists in localStorage
      ...(storedSelectedTransactionsData && { selectedTransactionsData: storedSelectedTransactionsData }),
    };

    logger.trackAiEvent({
      name: AI_EVENTS.AI_RESUME_CHAT,
      selection: resumeAiSessionId,
    });

    setConnectConfig(config);

    aiMessageCounter.value = (messages.length - 1) % AI_MESSAGE_LIMIT;

    // Add all messages from the resumed chat
    const messagesToAdd = messages.map((msg: ResumeAIChatMsg) => {
      const isAiMessage = msg.isAiMessage;
      const timestamp = (msg.createdAt ? new Date(msg.createdAt).valueOf() : Date.now() + 1) as number;

      if (isAiMessage) {
        return createSystemMessageFromAI({
          message: msg.content,
          sessionId: msg.aiSessionId,
          timestamp,
        });
      }
      return createUserMessageForAI({
        message: msg.content,
        sessionId: msg.aiSessionId,
        timestamp,
      });
    });

    conversation.addItems(...messagesToAdd);

    // Activate AI flow with the resumed session
    startAiSession(resumeAiSessionId || undefined);

    // Handle the last message action if it exists
    if (lastMessage.action) {
      handleAiResponseAction({
        action: lastMessage.action,
        metadata: { message: lastMessage.content, aiResponse: lastMessage },
      });
    }
  };

  const onConnectPreSupportSelection = (event: PreSupportSelectionEvent) => {
    // TODO: determine which message to send to AI
    connectToAiWithWelcome({ event, entryText: 'I need help' });
  };

  function onStartSelectedEntityFlow(event: StartSelectedEntityFlowEvent) {
    const { selectedEntity, supportOptions } = event.payload;

    devLog.debug('[CF-v1] Starting selected entity flow', { selectedEntity, supportOptions });

    // Set connect config for the flow
    setConnectConfig(supportOptions);

    // Render the flow intro as an AI welcome message (same as legacy AiService.handleStartSelectedEntityFlow,
    // which passes the selected-entity text as the connectToAI aiMessage override). No API at this point.
    if (
      !connectToAiWithWelcome({
        event,
        entryText: null,
        aiMessageOverride: tConversation('chat.sys.msg.ai.selected_entity_flow') as string,
      })
    ) {
      return;
    }

    disableUserInputForAi();

    // Show transaction selector message with the pre-selected entity
    // The entity will be shown as the first item in the list
    conversation.addItems(
      createTransactionSelectMessage({
        config: { connect: connectConfig.value },
        txSubtypes: getExternalTransactionTypes(selectedEntity.type),
        preSelectedTransactions: [selectedEntity.entity],
        singleSelectOnly: true,
      })
    );
  }

  function onStartKYCFlow(event: StartKYCFlowEvent) {
    const { failedKycMessage, supportOptions } = event.payload;
    devLog.debug('[CF-v1] Starting KYC flow', { failedKycMessage, supportOptions });

    if (!failedKycMessage) {
      captureEventException(
        new Error('Failed KYC flow started without failedKycMessage'),
        ErrorTrackingEvent.FailedKycConfig
      );
      return;
    }

    setConnectConfig(supportOptions ?? hardcodedConnectConfig);
    aiFlow.value.inKycFlow = true;

    // Show the welcome first (no API), then submit the failed-KYC message as the first real request,
    // which carries welcomeMessage + in_kyc_flow. Mirrors legacy AiService.handleStartKYCFlow.
    if (connectToAiWithWelcome({ event, entryText: null })) {
      submitUserMessageForAI(failedKycMessage);
    }
  }

  // TODO: clean up handlers to be separate variables with clear points of which use for which case
  // Current structure is not clear and needs to be improved

  // Switchers for handling different AI events
  const aiEventSwitch = switcher<BaseEvent | BaseExternalEvent>()
    // AI TODO: add support later if needed
    // .when(EventType.isRateAiResponseEvent, handleRateAiResponseEvent)
    .when(EventType.isUserMessageForAIEvent, handleMessageFromUserForAI)
    .when(EventType.isUserFileForAIEvent, handleFileFromUserForAI)
    .when(isUserLoginFromAIEvent, handleUserLoginFromAIResolution)
    .none((e) => globalLogger.error('[AiS] Unhandled event passed', { ...e }));

  const aiConnectEventSwitch = switcher<BaseEvent | BaseExternalEvent>()
    // Section & Browse other articles will be handled in useFlowEventHandlers.ts, just show more data
    // .when(isSectionOptionEvent, onSectionSelectOption)
    // .when(isBrowseOtherArticlesEventSupported, onArticleSelectOption)
    .when(EventType.isUserSelectedTransactionListEvent, handleUserTransactionMetadata)
    .when(isArticleOptionEvent, onArticleSelectOption)
    .when(isArticleFromListNavigationEvent, onArticleSelectOption)
    .when(isIssueNotListedArticlesEvent, onIssueNotListedArticles)
    .when(isOverlongResolutionEvent, handleOverlongAiConversationResolution)
    .when(isStaleConversationResolutionEvent, handleStaleAiConversation)
    .when(isConnectPreSupportSelectionEvent, onConnectPreSupportSelection)
    .none((e) => globalLogger.error('[AiS] Unhandled connect event passed', { ...e }));

  const aiConnectExternalEventSwitch = switcher<BaseExternalEvent>()
    .when(EventType.isUserLoginEvent, handleUserLoginExternalEvent)
    .when(EventType.isGuestLoginEvent, handleGuestLoginExternalEvent)
    .when(isArticleSupportRequestEvent, handleAiFlowFromArticle)
    .when(EventType.isResumeAIChat, handleResumeAIChat)
    .when(isStartSelectedEntityFlowEvent, onStartSelectedEntityFlow)
    .when(isStartKYCFlowEvent, onStartKYCFlow)
    .none((e) => globalLogger.error('[AiS] Unhandled connect external event passed', { ...e }));

  function testAiEventHandler(event: BaseEvent | BaseExternalEvent) {
    return aiEventSwitch.test(event);
  }

  function submitAiEvent(event: BaseEvent | BaseExternalEvent) {
    if (tryRedirectGuestBeforeAi(event)) {
      return;
    }
    aiEventSwitch.match(event);
  }

  function testAiConnectEventHandler(event: BaseEvent) {
    if (!checkEventCanConnectToAI(event)) return false;
    return aiConnectEventSwitch.test(event);
  }

  function submitAiConnectEvent(event: BaseEvent) {
    // We have to check articles for Chat Triggers first, other events are good to be handled by general check
    const isSkipGuestRedirectEvent = [isArticleOptionEvent, isArticleFromListNavigationEvent].some((check) =>
      check(event)
    );

    if (checkEventCanConnectToAI(event) && !isSkipGuestRedirectEvent && tryRedirectGuestBeforeAi(event)) {
      return;
    }

    aiConnectEventSwitch.match(event);
  }

  function testAiConnectExternalEventHandler(event: BaseExternalEvent) {
    const ongoingAiFlowEvents = [EventType.isUserLoginEvent];
    const isOngoingAiFlowEvent = ongoingAiFlowEvents.some((check) => check(event));

    // If AI flow is active, don't allow to connect to AI again
    if (aiFlow.value.active && !isOngoingAiFlowEvent) return false;

    if (!isAiEligibleExternalEvent(event)) return false;
    if (!checkEventCanConnectToAI(event)) return false;

    return aiConnectExternalEventSwitch.test(event);
  }

  function submitAiConnectExternalEvent(event: BaseExternalEvent) {
    aiConnectExternalEventSwitch.match(event);
  }

  /**
   * Re-dispatches the connect-to-AI event that was deferred by {@link tryRedirectGuestBeforeAi}.
   * After guest details or full login, {@link tryRedirectGuestBeforeAi} no longer applies and normal handlers run.
   */
  function replayPendingAiConnectEvent() {
    const pending = pendingAiConnectEvent.value;
    if (!pending) {
      return;
    }
    pendingAiConnectEvent.value = null;
    if (EventType.isExternalEvent(pending)) {
      submitAiConnectExternalEvent(pending);
    } else if (testAiConnectEventHandler(pending)) {
      submitAiConnectEvent(pending);
    } else {
      submitAiEvent(pending);
    }
  }

  return {
    aiFlow,
    resetAiFlow,
    restartAiFlow,
    checkCanConnectToAI,
    disableUserInputForAi,
    submitUserMessageForAI,
    testAiEventHandler,
    submitAiEvent,
    testAiConnectEventHandler,
    submitAiConnectEvent,
    testAiConnectExternalEventHandler,
    submitAiConnectExternalEvent,

    onIntervention: interventionHook.on,
    onAiFlowStart: aiFlowStartedHook.on,
    onEndAiChat: endAiChatHook.on,
  };
}
